
import MainButton from '@/core/components/MainButton.vue';
import SecondaryButton from '@/core/components/SecondaryButton.vue';
import Spinner from '@/core/components/Spinner.vue';
import { Machine } from '@/models/bookings';
import * as TelliqService from '@/services/telliq.service';
import { GETTERS } from '@/store/getters';
import { MUTATIONS } from '@/store/mutations';
import { emitter } from '@/utils/constants';
import { bookingEndDateReconstruction, bookingStartDateReconstruction } from '@/utils/functions';
import moment from 'moment';
import Vue from 'vue';
import { mapGetters } from 'vuex';

interface Filter {
  name: string;
  active: boolean;
  handler: (machine: Machine) => boolean;
}

export default Vue.extend({
  data() {
    return {
      loading: false,
      filters: [
        {
          name: this.$t('bookings.vehicle_type.all'),
          active: true,
          handler: () => {
            return true;
          },
        },
        {
          name: this.$t('bookings.vehicle_type.private'),
          active: false,
          handler: (machine) => {
            return machine.isPrivateBookingEnabled;
          },
        },
        {
          name: this.$t('bookings.vehicle_type.business'),
          active: false,
          handler: (machine) => {
            return !machine.isPrivateBookingEnabled;
          },
        },
      ] as Filter[],
      machines: [] as Machine[],
    };
  },

  components: {
    MainButton,
    SecondaryButton,
    Spinner,
  },

  computed: {
    ...mapGetters({
      booking: GETTERS.GET_CREATE_BOOKING,
    }),

    getActiveFilter(): Filter | undefined {
      return this.filters.find((filter) => filter.active);
    },

    getMachines(): Machine[] {
      const filter = this.getActiveFilter;
      return filter ? this.machines.filter(filter.handler) : this.machines;
    },
  },

  async created() {
    this.$store.commit(MUTATIONS.SET_HEADER, { title: this.$t('bookings.headers.select_car'), subtitle: '' });
    this.loading = true;
    try {
      const start_date = moment(bookingStartDateReconstruction(this.booking)).add(1, 'second').toISOString();
      const end_date = moment(bookingEndDateReconstruction(this.booking)).toISOString();
      const [machines, bookings] = await Promise.all([
        TelliqService.getMachines(),
        TelliqService.getBookings(start_date, end_date),
      ]);
      // Filter out all the machines having a booking in the requested time slot.
      const available_machines = machines.filter(
        (machine) => bookings.length === 0 || !bookings.some((booking) => booking.machine.id === machine.id),
      );

      // filter out check-in vehicles that cannot be booked via regular booking flow
      this.machines = available_machines.filter((machine) => !machine.isEnabledCheckingFunctionality);
    } catch (e) {
      emitter.emit('notify', {
        text: this.$t('core.errors.something_went_wrong'),
        color: 'error',
      });
    }
    this.loading = false;
  },

  methods: {
    filterSelected(index: number): void {
      const current_filter = this.filters[index];
      current_filter.active = true;

      this.filters.filter((_, idx) => idx !== index).forEach((filter) => (filter.active = false));
    },

    onSelectedCar(machine: Machine): void {
      this.booking.machine = { id: machine.id, name: machine.name };
      this.booking.canBePrivate = machine.isPrivateBookingEnabled;
      this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, this.booking);
      this.$router.push('/booking/car-selection-details');
    },

    onStartDateClicked(): void {
      this.$router.push('/booking/start-date');
    },

    onEndDateClicked(): void {
      this.$router.push('/booking/end-date');
    },
  },
});
